// NO importar desde '../services/api' aquí: crea dependencia circular con apiClient.
// systemService se obtiene por import dinámico dentro de getApiTimeouts.

// Valores por defecto (fallback si no se puede obtener de la configuración)
const DEFAULT_TIMEOUTS = {
  default: 60000, // 60 segundos
  petOperations: {
    desktop: 60000, // 60 segundos
    mobile: 120000, // 120 segundos
  },
  fileUpload: {
    baseDesktop: 60000, // 60 segundos
    baseMobile: 120000, // 120 segundos
    secondsPerMB: 10,
    max: 180000, // 180 segundos (3 minutos)
  },
};

// Cache para evitar múltiples llamadas
let timeoutCache: typeof DEFAULT_TIMEOUTS | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene los timeouts configurados desde el sistema
 * Usa cache para evitar múltiples llamadas
 */
export const getApiTimeouts = async (): Promise<typeof DEFAULT_TIMEOUTS> => {
  const now = Date.now();
  
  // Retornar cache si está vigente
  if (timeoutCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return timeoutCache;
  }

  try {
    const { systemService } = await import('../services/api');
    const [
      defaultTimeout,
      petDesktop,
      petMobile,
      fileDesktop,
      fileMobile,
      secondsPerMB,
      fileMax,
    ] = await Promise.allSettled([
      systemService.getPublicSetting('api_timeout_default_seconds'),
      systemService.getPublicSetting('api_timeout_pet_operations_desktop_seconds'),
      systemService.getPublicSetting('api_timeout_pet_operations_mobile_seconds'),
      systemService.getPublicSetting('api_timeout_file_upload_base_desktop_seconds'),
      systemService.getPublicSetting('api_timeout_file_upload_base_mobile_seconds'),
      systemService.getPublicSetting('api_timeout_file_upload_seconds_per_mb'),
      systemService.getPublicSetting('api_timeout_file_upload_max_seconds'),
    ]);

    const parseValue = (result: PromiseSettledResult<any>, defaultValue: number): number => {
      if (result.status === 'fulfilled') {
        const value = result.value.value;
        const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
        return !isNaN(parsed) && parsed > 0 ? parsed * 1000 : defaultValue; // Convertir a ms
      }
      return defaultValue;
    };

    timeoutCache = {
      default: parseValue(defaultTimeout, DEFAULT_TIMEOUTS.default),
      petOperations: {
        desktop: parseValue(petDesktop, DEFAULT_TIMEOUTS.petOperations.desktop),
        mobile: parseValue(petMobile, DEFAULT_TIMEOUTS.petOperations.mobile),
      },
      fileUpload: {
        baseDesktop: parseValue(fileDesktop, DEFAULT_TIMEOUTS.fileUpload.baseDesktop),
        baseMobile: parseValue(fileMobile, DEFAULT_TIMEOUTS.fileUpload.baseMobile),
        secondsPerMB: parseValue(secondsPerMB, DEFAULT_TIMEOUTS.fileUpload.secondsPerMB) / 1000, // Mantener en segundos
        max: parseValue(fileMax, DEFAULT_TIMEOUTS.fileUpload.max),
      },
    };

    cacheTimestamp = now;
    return timeoutCache;
  } catch (error) {
    console.warn('Error obteniendo timeouts del sistema, usando valores por defecto:', error);
    return DEFAULT_TIMEOUTS;
  }
};

/**
 * Detecta si el dispositivo es móvil
 */
export const isMobileDevice = (): boolean => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768
  );
};

/**
 * Calcula el timeout para subida de archivos basado en tamaño
 */
export const calculateFileUploadTimeout = async (
  fileSizeBytes: number
): Promise<number> => {
  const timeouts = await getApiTimeouts();
  const isMobile = isMobileDevice();
  const baseTimeout = isMobile ? timeouts.fileUpload.baseMobile : timeouts.fileUpload.baseDesktop;
  
  const fileSizeMB = fileSizeBytes / (1024 * 1024);
  const sizeBasedTimeout = Math.min(
    fileSizeMB * timeouts.fileUpload.secondsPerMB * 1000, // Convertir a ms
    timeouts.fileUpload.max
  );
  
  return Math.max(baseTimeout, sizeBasedTimeout);
};

